import { default as deserialize } from 'app/utils/deserialize'
import { fetch, create, update, remove } from 'app/utils/data'
import get from 'lodash/get'
import FormErrors from 'app/utils/form_errors'
import sortBy from 'lodash/sortBy'
import moment from 'moment'
import semver from 'semver'
import { resolve, latest } from 'app/releases'

/**
 * After our 6.7.0 release, we found a lot of issues with the changes we made
 * and old asset templates. To reduce the support requests and explain to folks
 * what's going on, we disabled saving Kits if we detect that the save might fail.
 */
export const kitSaveWillFail671 = (kit) => {
  if (typeof kit !== 'object') {
    // We don't have a Kit yet or this mixin is being used in a component
    // that does not expose the Kit as a prop.
    return false
  }

  // If we have icon uploads or any kind of subsetting AND the version
  // is not 6.x we are going to halt saving.
  const hasIconUploads = Array.isArray(kit.iconUploads) && kit.iconUploads.length > 0
  const hasSubset = kit.subset !== null
  const hasPackages = kit.kitPackageEnabled
  const isExtra = hasIconUploads || hasSubset || hasPackages

  const resolvedVersion = resolve({ version: kit.version })
  const isVersionOlderThanLatest = semver.lt(resolvedVersion, latest.v6.version)

  return isExtra && isVersionOlderThanLatest
}

export default {
  namespaced: true,

  state: {
    subsetEnabledMsgFlag: true,
    list: [],
    activeList: [],
    pendingList: [],
    item: null
  },

  getters: {
    subsetEnabledMessageFlag: (state) => state.subsetEnabledMsgFlag,
    currentKits: (state) => state.list,
    currentKit: (state) => state.item,
    activeKits: (state) => state.activeList,
    pendingKits: (state) => state.pendingList,

    defaultKitProps: () => {
      return {
        // intentionally omitting autoAccessibilityEnabled
        // in order to defer to the back end business logic,
        // because the default differs between 6.x and later
        licenseSelected: 'free',
        technologySelected: 'webfonts',
        version: '7.x',
        asynchronousLoadingEnabled: false,
        shimEnabled: true,
        v4FontFaceShimEnabled: true,
        useIntegrityHash: false,
        detectConflictsUntil: null,
        subset: null,
        kitPackageEnabled: false,
        kitIcon: null,
        kitColor: null
      }
    },
    sortedKits: (state) => {
      const val = sortBy(state.activeList, (k) => moment.utc(k.updatedAt).unix())

      val.reverse()

      return val
    },

    sortedKitsAlphabetical: (state) => {
      const val = sortBy(state.activeList, (k) => k.name.toLowerCase())
      return val
    },

    sortedKitsPageviews: (state) => {
      const val = sortBy(state.activeList, (k) => k.pageviews)
      return val
    }
  },

  mutations: {
    setSubsetEnabledMessageFlag(state, { flag }) {
      state.subsetEnabledMsgFlag = flag
    },

    kitsFetched(state, { kits }) {
      state.list = kits
      state.pendingList = state.list.filter((k) => k.status === 'pending')
      state.activeList = state.list.filter((k) => k.status !== 'pending')
    },

    kitFetched(state, { kit }) {
      const newList = state.list.reduce((acc, curr) => {
        kit.id === curr.id ? acc.push(kit) : acc.push(curr)

        return acc
      }, [])

      state.list = newList
      state.item = kit
    },

    kitDeleted(state, { kitToken }) {
      if (get(state, ['item', 'token']) === kitToken) {
        state.item = null
      }

      state.list = state.list.filter((kit) => {
        return kit.token !== kitToken
      })
    },

    resetState(state) {
      state.list.length = 0
      state.pendingList.length = 0
      state.activeList.length = 0
      state.item = null
    }
  },

  actions: {
    setSubsetEnabledMessageFlag({ commit, state }, flag) {
      commit('setSubsetEnabledMessageFlag', { flag })
    },

    async fetchKits({ commit }, { page = 1, filters = {} } = {}) {
      try {
        const response = await fetch({ url: '/api/clouds/kits' })
        const kits = await deserialize(response.data)

        commit('kitsFetched', { kits })
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async fetchKit({ commit }, kitToken) {
      try {
        const response = await fetch({ url: `/api/clouds/kits/${kitToken}` })
        const kit = await deserialize(response.data)

        commit('kitFetched', { kit })
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async createKit({ commit }, changes) {
      try {
        const response = await create({
          url: '/api/clouds/kits',
          type: 'kit',
          data: changes
        })

        const kit = await deserialize(response.data)

        commit('kitFetched', { kit })

        return kit
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async copyKit({ commit }, token) {
      try {
        const response = await create({
          url: '/api/clouds/kits/copy',
          type: 'kit',
          data: { token: token }
        })

        const kit = await deserialize(response.data)

        commit('kitFetched', { kit })

        return kit
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async updateKit({ commit, state }, changes) {
      try {
        const response = await update({
          url: `/api/clouds/kits/${state.item.token}`,
          type: 'kit',
          record: state.item,
          changes
        })

        const kit = await deserialize(response.data)

        commit('kitFetched', { kit })

        return kit
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async deleteKit({ commit }, kitToken) {
      try {
        await remove({
          url: `/api/clouds/kits/${kitToken}`,
          type: 'kit'
        })

        commit('kitDeleted', { kitToken })
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async deletePendingKits(context) {
      try {
        const response = await fetch({ url: '/api/clouds/kits' })
        const kits = await deserialize(response.data)

        kits
          .filter((k) => k.status === 'pending')
          .forEach((kit) => {
            context.dispatch('deleteKit', kit.token)
          })
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async validateDomain({ commit }, domainData) {
      const domain = domainData.domain
      const listLength = domainData.listLength

      try {
        const response = await create({
          url: '/api/clouds/kits/validate-domain',
          type: 'kit',
          data: { domain, listLength }
        })

        return { success: response.data.success }
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async transformSubset({ commit }, data) {
      try {
        const response = await create({
          url: '/api/clouds/kits/subset-validation',
          type: 'subset-validation',
          data: { icons: data }
        })

        return response.data.data.attributes.icons
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    }
  }
}
